// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Project } from '@/project/api/types';
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';

interface Props {
  /**
   * The project containing workflow information
   */
  project: Pick<Project, 'activeWorkflow'>;
}

/**
 * A compact workflow stage indicator that displays workflow progress as dots.
 * Shows completed stages in green, current stage in blue, and remaining stages in gray.
 * Displays the current stage number/total stages and the current stage name.
 */
export function WorkflowStageIndicator(props: Props) {
  const theme = useTheme();

  if (!props.project.activeWorkflow) {
    return (
      <Typography variant="body2" color="text.secondary">
        No workflow
      </Typography>
    );
  }

  // Sort stages by their order property for proper display
  const sortedStages = [...props.project.activeWorkflow.stages].sort(
    (a, b) => a.order - b.order
  );

  // Find the active stage
  const activeStage = props.project.activeWorkflow.stages.find(
    (stage) => stage.active
  );

  if (!activeStage) {
    return (
      <Stack
        spacing={0.5}
        alignItems="center"
        sx={{ width: 'fit-content', margin: '0 auto' }}
      >
        <Typography variant="body2" color="text.secondary">
          {props.project.activeWorkflow.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No active stage
        </Typography>
      </Stack>
    );
  }

  const totalStages = props.project.activeWorkflow.stages.length;
  const activeStageOrder = activeStage.order;
  // Calculate stage number based on how many stages have lower order values
  const currentStageNumber = sortedStages.filter(
    (stage) => stage.order <= activeStageOrder
  ).length;

  return (
    <Stack
      spacing={0.5}
      alignItems="center"
      sx={{ width: 'fit-content', margin: '0 auto' }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        {/* Dots representing workflow stages */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {sortedStages.map((stage) => {
            let dotColor: string;

            if (stage.order < activeStageOrder) {
              // Completed stages - green
              dotColor = theme.palette.success.main;
            } else if (stage.order === activeStageOrder) {
              // Current stage - blue
              dotColor = theme.palette.primary.main;
            } else {
              // Remaining stages - gray
              dotColor = theme.palette.grey[400];
            }

            const tooltipTitle = `${stage.name}`;

            return (
              <Tooltip
                key={stage.id}
                title={tooltipTitle}
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: dotColor,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            );
          })}
        </Stack>

        {/* Current stage number and total stages */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.2 }}
        >
          {currentStageNumber}/{totalStages}
        </Typography>
      </Stack>

      {/* Current stage name */}
      <Typography
        variant="body2"
        sx={{
          lineHeight: 1.2,
          fontWeight: 500,
        }}
      >
        {activeStage.name}
      </Typography>
    </Stack>
  );
}
